import { type UploadFile } from 'antd';
import { useCallback, useRef, useState } from 'react';
import { ALLOWED_PREVIEW_TYPES } from 'src/constants/fileTypes';
import { getBase64Img } from 'src/utils/image/getBase64Src';

export function usePreviewDocument(loadPreview?: (file: UploadFile) => Promise<string | undefined>) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const loadedPreviews = useRef<Record<string, string>>({});
  const loadingFileUid = useRef<string | null>(null);

  const handleCancel = useCallback(() => {
    loadingFileUid.current = null;
    setPreviewLoading(false);
    setPreviewOpen(false);
  }, []);

  const handlePreview = useCallback(async (file: UploadFile) => {
    if (!file.preview && file.originFileObj) {
      const base64Img = await getBase64Img(file, ALLOWED_PREVIEW_TYPES);

      file.preview = base64Img;
    }

    let preview: string | undefined = file.preview ?? file.url ?? loadedPreviews.current[file.uid];

    // Documents loaded from the server come without content, it is fetched when the user opens them
    if (!preview && loadPreview) {
      loadingFileUid.current = file.uid;
      setPreviewTitle(file.name);
      setPreviewImage('');
      setPreviewLoading(true);
      setPreviewOpen(true);

      preview = await loadPreview(file).catch(() => undefined);

      if (preview) {
        loadedPreviews.current[file.uid] = preview;
      }

      // The preview was closed or another document was opened while this one was loading
      if (loadingFileUid.current !== file.uid) return;

      loadingFileUid.current = null;
      setPreviewLoading(false);
    }

    const isPreviewAllowed = ALLOWED_PREVIEW_TYPES.some((item) => preview?.includes(item));

    if (preview && isPreviewAllowed) {
      setPreviewImage(preview);
      setPreviewTitle(file.name);
      setPreviewOpen(true);
    } else {
      setPreviewOpen(false);
    }
  }, [loadPreview]);

  return { previewOpen, previewImage, previewTitle, previewLoading, handlePreview, handleCancel };
}
